import { useEffect, useMemo, useState } from "react";
import {
  AlertCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DownloadIcon,
  XIcon,
} from "lucide-react";
import type {
  PayrollUiPayPeriod,
  PayrollUiProviderTemplateRef,
} from "../../types";

type ExportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  period: PayrollUiPayPeriod;
  providerTemplateRefs?: PayrollUiProviderTemplateRef[];
  incompleteCount: number;
  onConfirm?: (input: {
    providerTemplateId: string;
    exportFormat: string;
    selectedFormat: string;
    allowIncomplete: boolean;
  }) => void | Promise<void>;
};

type ExportFormatOption = {
  providerTemplateId: string;
  provider: string;
  exportFormat: string;
  name: string;
  description: string;
};

const FALLBACK_EXPORT_FORMATS: readonly ExportFormatOption[] = [
  {
    providerTemplateId: "provider-intellio-standard",
    provider: "Intellio",
    exportFormat: "csv",
    name: "Intellio Standard Format",
    description: "Default format with all compensation components",
  },
  {
    providerTemplateId: "provider-adp-csv",
    provider: "ADP",
    exportFormat: "csv",
    name: "ADP Template",
    description: "Pre-configured for ADP Workforce Now",
  },
  {
    providerTemplateId: "provider-paychex-flex",
    provider: "Paychex",
    exportFormat: "csv",
    name: "Paychex Template",
    description: "Pre-configured for Paychex Flex",
  },
  {
    providerTemplateId: "provider-gusto",
    provider: "Gusto",
    exportFormat: "csv",
    name: "Gusto Template",
    description: "Pre-configured for Gusto",
  },
  {
    providerTemplateId: "provider-onpay",
    provider: "OnPay",
    exportFormat: "csv",
    name: "OnPay Template",
    description: "Pre-configured for OnPay",
  },
] as const;

const FALLBACK_EXPORT_FORMAT_INDEX = new Map(
  FALLBACK_EXPORT_FORMATS.map((format) => [format.providerTemplateId, format]),
);

const FALLBACK_EXPORT_FORMAT_BY_PROVIDER = new Map(
  FALLBACK_EXPORT_FORMATS.map((format) => [format.provider.toLowerCase(), format]),
);

const INTELLIO_COLUMNS = [
  "Employee ID",
  "First Name",
  "Last Name",
  "Pay Period Start",
  "Pay Period End",
  "Employment Type",
  "Track",
  "Base Pay - Judaic",
  "Base Pay - General",
  "Parsonage - Tax Exempt",
  "Stipend - Coaching",
  "Stipend - Curriculum Development",
  "Hourly Pay (hours)",
  "Hourly Pay (rate)",
  "Hourly Pay (gross)",
  "Adjustments (net)",
  "Attendance Bonus",
  "Absence Penalty",
  "External Tuition (amount)",
  "External Tuition (school)",
  "External Tuition (tax treatment)",
  "Tuition Remission Taxable",
  "Deductions - Health Insurance (Pre-Tax)",
  "Deductions - 401k/403b (Pre-Tax)",
  "Deductions - Tzedakah (Post-Tax)",
  "Gross Total",
  "Total Deductions",
  "Net Pay Estimate",
];

export function ExportModal({
  isOpen,
  onClose,
  period,
  providerTemplateRefs,
  incompleteCount,
  onConfirm,
}: ExportModalProps) {
  const [selectedProviderTemplateId, setSelectedProviderTemplateId] = useState("");
  const [allowIncomplete, setAllowIncomplete] = useState(false);
  const [showColumnPreview, setShowColumnPreview] = useState(false);

  const exportFormats = useMemo<ExportFormatOption[]>(() => {
    if (!providerTemplateRefs?.length) {
      return [...FALLBACK_EXPORT_FORMATS];
    }

    return providerTemplateRefs.map((template) => {
      const fallbackById = FALLBACK_EXPORT_FORMAT_INDEX.get(template.id);
      const fallbackByProvider = FALLBACK_EXPORT_FORMAT_BY_PROVIDER.get(
        template.provider.toLowerCase(),
      );
      const fallback = fallbackById ?? fallbackByProvider;
      const exportLabel = template.exportFormat.toUpperCase();
      return {
        providerTemplateId: template.id,
        provider: template.provider,
        exportFormat: template.exportFormat,
        name: fallback?.name ?? template.templateName,
        description:
          fallback?.description
          ?? `${template.provider} ${exportLabel} export using ${template.templateName}`,
      };
    });
  }, [providerTemplateRefs]);

  useEffect(() => {
    const nextSelectedProviderTemplateId =
      exportFormats.find(
        (format) => format.providerTemplateId === selectedProviderTemplateId,
      )?.providerTemplateId
      ?? exportFormats[0]?.providerTemplateId
      ?? "";
    if (nextSelectedProviderTemplateId !== selectedProviderTemplateId) {
      setSelectedProviderTemplateId(nextSelectedProviderTemplateId);
    }
  }, [exportFormats, selectedProviderTemplateId]);

  if (!isOpen) {
    return null;
  }

  const canExport = incompleteCount === 0 || allowIncomplete;
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);

  const selectedExportFormat =
    exportFormats.find(
      (format) => format.providerTemplateId === selectedProviderTemplateId,
    ) ?? exportFormats[0];
  const showIntellioColumnPreview =
    Boolean(selectedExportFormat)
    && selectedExportFormat.provider.toLowerCase() === "intellio";

  const handleConfirm = async () => {
    if (!canExport || !selectedExportFormat) {
      return;
    }
    await onConfirm?.({
      providerTemplateId: selectedExportFormat.providerTemplateId,
      exportFormat: selectedExportFormat.exportFormat,
      selectedFormat: selectedExportFormat.providerTemplateId,
      allowIncomplete,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative flex max-h-[90vh] w-full max-w-[700px] flex-col overflow-hidden rounded-[12px] bg-surface-light shadow-xl animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between border-b border-border bg-muted/30 p-5">
          <div>
            <h2 className="font-heading text-lg font-semibold text-iron-grey">
              Export Payroll
            </h2>
            <p className="mt-0.5 font-body text-sm text-text-secondary">
              {period.label}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary transition-colors hover:text-iron-grey"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6 overflow-y-auto p-6">
          <div className="rounded-[8px] bg-muted p-4">
            <h3 className="mb-3 font-body text-sm font-semibold text-iron-grey">
              Period Summary
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="mb-1 font-body text-xs text-text-secondary">
                  Total Employees
                </p>
                <p className="font-body text-lg font-semibold text-iron-grey">
                  {period.totalEmployees}
                </p>
              </div>
              <div>
                <p className="mb-1 font-body text-xs text-text-secondary">
                  Complete
                </p>
                <p className="font-body text-lg font-semibold text-jungle">
                  {period.employeesComplete}
                </p>
              </div>
              <div>
                <p className="mb-1 font-body text-xs text-text-secondary">
                  Gross Total
                </p>
                <p className="font-body text-lg font-semibold text-primary">
                  {formatCurrency(period.totalGross)}
                </p>
              </div>
            </div>
          </div>

          {incompleteCount > 0 ? (
            <div className="flex items-start gap-3 rounded-r-[8px] border-l-4 border-coral bg-warning-background p-4">
              <AlertCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-coral" />
              <div className="flex-1">
                <h3 className="font-body text-sm font-semibold text-iron-grey">
                  Incomplete Data Detected
                </h3>
                <p className="mt-1 font-body text-sm text-text-secondary">
                  {incompleteCount}{" "}
                  {incompleteCount === 1 ? "employee has" : "employees have"}{" "}
                  incomplete data. Exported records will be flagged in the CSV.
                </p>
                <label className="mt-3 flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={allowIncomplete}
                    onChange={(event) =>
                      setAllowIncomplete(event.target.checked)
                    }
                    className="h-4 w-4 cursor-pointer rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="font-body text-sm text-iron-grey">
                    Export with incomplete data
                  </span>
                </label>
              </div>
            </div>
          ) : null}

          <div>
            <h3 className="mb-3 font-body text-sm font-semibold text-iron-grey">
              Export Format
            </h3>
            <div className="space-y-2">
              {exportFormats.map((format) => (
                <div key={format.providerTemplateId}>
                  <label
                    className={`flex cursor-pointer items-start gap-3 rounded-[8px] border p-4 transition-colors ${
                      selectedProviderTemplateId === format.providerTemplateId
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted"
                    }`}
                  >
                    <input
                      type="radio"
                      name="format"
                      value={format.providerTemplateId}
                      checked={
                        selectedProviderTemplateId === format.providerTemplateId
                      }
                      onChange={(event) =>
                        setSelectedProviderTemplateId(event.target.value)
                      }
                      className="mt-0.5 cursor-pointer text-primary focus:ring-primary"
                    />
                    <div className="flex-1">
                      <p className="font-body text-sm font-semibold text-iron-grey">
                        {format.name}
                      </p>
                      <p className="mt-0.5 font-body text-xs text-text-secondary">
                        {format.description}
                      </p>
                    </div>
                  </label>

                  {showIntellioColumnPreview
                  && format.providerTemplateId === selectedExportFormat?.providerTemplateId ? (
                    <div className="ml-7 mt-2 overflow-hidden rounded-[8px] border border-border">
                      <button
                        onClick={() =>
                          setShowColumnPreview((current) => !current)
                        }
                        className="flex w-full items-center justify-between bg-muted/30 p-3 transition-colors hover:bg-muted"
                      >
                        <span className="font-body text-xs font-medium text-iron-grey">
                          Preview Columns ({INTELLIO_COLUMNS.length})
                        </span>
                        {showColumnPreview ? (
                          <ChevronUpIcon className="h-4 w-4 text-text-secondary" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4 text-text-secondary" />
                        )}
                      </button>

                      {showColumnPreview ? (
                        <div className="max-h-[300px] overflow-y-auto bg-surface-light p-4">
                          <div className="flex flex-wrap gap-2">
                            {INTELLIO_COLUMNS.map((column) => (
                              <span
                                key={column}
                                className="inline-flex items-center rounded-[6px] border border-border bg-muted px-2.5 py-1 font-body text-xs text-iron-grey"
                              >
                                {column}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-border bg-background-light p-5">
          <button
            onClick={onClose}
            className="rounded-[6px] border border-border bg-surface-light px-4 py-2 font-body text-sm font-medium text-iron-grey transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          <button
            disabled={!canExport || !selectedExportFormat}
            onClick={() => void handleConfirm()}
            data-testid="payroll-export-confirm"
            className="flex items-center gap-2 rounded-[6px] bg-primary px-4 py-2 font-body text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            <DownloadIcon className="h-4 w-4" />
            Generate Export
          </button>
        </div>
      </div>
    </div>
  );
}
